/**
 * CLIENTE API COM AUTENTICAÇÃO
 * ============================
 * Cliente HTTP que automaticamente adiciona tokens de autenticação
 */

import { supabase } from './supabase-client'

interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  body?: any
  headers?: Record<string, string>
}

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl
  }

  // Obter token de autenticação
  private async getAuthToken(): Promise<string | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      return session?.access_token || null
    } catch (error) {
      console.error('❌ [API-CLIENT] Erro ao obter token:', error)
      return null
    }
  }

  // Fazer requisição autenticada
  async request<T = any>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {} } = options
    
    // Obter token de autenticação
    const token = await this.getAuthToken()
    
    // Configurar headers
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers
    }

    // Adicionar token se disponível
    if (token) {
      requestHeaders['Authorization'] = `Bearer ${token}`
    }

    // Configurar body
    const requestBody = body ? JSON.stringify(body) : undefined

    // Fazer requisição
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method,
      headers: requestHeaders,
      body: requestBody
    })

    // Verificar se a resposta é ok
    if (!response.ok) {
      const errorText = await response.text()
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`
      
      try {
        const errorJson = JSON.parse(errorText)
        errorMessage = errorJson.message || errorJson.error || errorMessage
      } catch {
        // Se não conseguir fazer parse, usar texto original
        if (errorText) {
          errorMessage = errorText
        }
      }
      
      throw new Error(errorMessage)
    }

    // Retornar resposta JSON
    try {
      return await response.json()
    } catch (error) {
      // Se não conseguir fazer parse do JSON, retornar texto
      return await response.text() as any
    }
  }

  // Métodos de conveniência
  async get<T = any>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', headers })
  }

  async post<T = any>(endpoint: string, body?: any, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body, headers })
  }

  async put<T = any>(endpoint: string, body?: any, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', body, headers })
  }

  async patch<T = any>(endpoint: string, body?: any, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'PATCH', body, headers })
  }

  async delete<T = any>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', headers })
  }
}

// Instância singleton do cliente API
export const apiClient = new ApiClient()

// Função utilitária para verificar se o usuário está autenticado
export async function isAuthenticated(): Promise<boolean> {
  try {
    // Verificar se temos sessão Supabase padrão
    const { data: { session } } = await supabase.auth.getSession()
    if (session?.access_token) {
      return true
    }

    // Fallback: Verificar se temos cookies customizados fazendo chamada à API
    // Os cookies httpOnly são enviados automaticamente nas requisições
    const response = await fetch('/api/auth/verify', {
      method: 'GET',
      credentials: 'include', // Importante para enviar cookies
    })

    if (response.ok) {
      const data = await response.json()
      return data.authenticated === true
    }

    return false
  } catch (error) {
    console.error('❌ [API-CLIENT] Erro ao verificar autenticação:', error)
    return false
  }
}

// Função utilitária para fazer logout
export async function logout(): Promise<void> {
  try {
    await supabase.auth.signOut()
  } catch (error) {
    console.error('❌ [API-CLIENT] Erro ao fazer logout:', error)
    throw error
  }
}

export default apiClient